
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FaEdit, FaTrash } from "react-icons/fa";
import toast from "react-hot-toast";
import EditCustomerForm from "./EditCustomerForm";
import { API_BASE_URL } from "../../../../config";

const CustomerTable = ({ search, data, setData, onViewVisits, onRefresh, adminPermissions, isSuperAdmin, page, totalPages, onPageChange }) => {
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [adminMap, setAdminMap] = useState({});
  const fetchingIds = useRef(new Set());
  const allCustomersCache = useRef(null);
  const [isFiltered, setIsFiltered] = useState(false);
  const [localPage, setLocalPage] = useState(1);

  const [filters, setFilters] = useState({
    customerName: "",
    customerNumber: "",
    followupDate: "",
    callType: "",
    customerFeedbackCategory: ""
  });

  // ✅ Permission check inside this component
  const hasPermission = (action) => {
    return isSuperAdmin || (adminPermissions.customer && adminPermissions.customer[action]);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    const fetchSubAdmin = async (id) => {
      if (!id || fetchingIds.current.has(id)) return;
      fetchingIds.current.add(id);
      const token = localStorage.getItem("token");
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/admin/get-subadmin/${id}`,
          { headers: { token } }
        );

        if (res.data.success && res.data.result?.subadmin) {
          setAdminMap((prev) => ({
            ...prev,
            [id]: res.data.result.subadmin.adminName,
          }));
        }
      } catch (error) {
        // console.error("Error fetching subadmin:", error);
      }
    };

    data.forEach((cust) => {
      const addedById =
        typeof cust.addedBy === "object" && cust.addedBy !== null
          ? cust.addedBy._id
          : cust.addedBy;

      if (addedById) fetchSubAdmin(addedById);
    });
  }, [data]);

  useEffect(() => {
    if (!search) {
      allCustomersCache.current = null;
      setFilteredCustomers(data);
      return;
    }

    const lowerSearch = search.toLowerCase();
    const doFilter = (allData) => {
      setFilteredCustomers(
        allData.filter((c) =>
          Object.values(c).some((val) =>
            val?.toString().toLowerCase().includes(lowerSearch)
          )
        )
      );
    };

    if (allCustomersCache.current) {
      doFilter(allCustomersCache.current);
      return;
    }

    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE_URL}/api/customer/get-all-customers?page=1&limit=99999`, { headers: { token } })
      .then((res) => {
        if (res.data.success) {
          allCustomersCache.current = res.data.result;
          doFilter(allCustomersCache.current);
        }
      })
      .catch(() => {});
  }, [search, data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setLocalPage(1); }, [filteredCustomers]);

  const applyFilters = async () => {
    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Applying filters...");

    try {
      const query = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key]) query[key] = filters[key];
      });

      // const queryString = new URLSearchParams(query).toString();
      const queryString = new URLSearchParams(query).toString().replace(/\+/g, "%20");

      const response = await axios.post(
        `${API_BASE_URL}/api/customer/filter-customers?${queryString}`,
        {},
        { headers: { token } }
      );

      toast.dismiss(loadingToast);

      if (response.data.success) {
        setFilteredCustomers(response.data.result);
        setIsFiltered(true);
        toast.success(
          `Filters applied! ${response.data.count} customers found`,
          { id: "success" }
        );
      } else {
        toast.error("No customers found for selected filters.");
      }
    } catch (error) {
      // console.error("Filter error:", error);
      toast.dismiss(loadingToast);
      toast.error("Something went wrong while filtering.");
    }
  };

  const resetFilters = () => {
    setFilters({
      customerName: "",
      customerNumber: "",
      followupDate: "",
      callType: "",
      customerFeedbackCategory: "",
    });
    setIsFiltered(false);
    setFilteredCustomers(data);
  };

  const confirmDelete = (id) => {
    setSelectedCustomerId(id);
    setShowDeleteModal(true);
  };

  const performDelete = async () => {
    const token = localStorage.getItem("token");
    const deletingToast = toast.loading("Deleting customer...");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/customer/delete-customer/${selectedCustomerId}`,
        {},
        { headers: { token } }
      );
      toast.dismiss(deletingToast);
      if (res.data.success) {
        toast.success("Customer deleted!", { id: "success" });
        onRefresh();
      } else {
        toast.error("Delete failed.");
      }
    } catch (error) {
      toast.dismiss(deletingToast);
      toast.error("Error deleting customer.");
    } finally {
      setShowDeleteModal(false);
      setSelectedCustomerId(null);
    }
  };

  const handleEditClick = (customer) => {
    setSelectedCustomer(customer);
    setShowEditForm(true);
  };

  const tableHeaders = [
    { key: "leadDate", label: "Lead Date" },
    { key: "leadFrom", label: "Lead From" },
    { key: "customerName", label: "Customer Name" },
    { key: "customerNumber", label: "Customer Number" },
    { key: "customerAddress", label: "Customer Address" },
    { key: "requiredFlatType", label: "Required Flat Type" },
    { key: "locationRequired", label: "Location Required" },
    { key: "requiredResaleOrNewFlat", label: "Resale/New Flat" },
    { key: "budget", label: "Budget" },
    { key: "customerFeedbackCategory", label: "Feedback Category" },
    { key: "callBackDateTime", label: "Call Back DateTime" },
    { key: "visitScheduleDateTime", label: "Visit Schedule DateTime" },
    { key: "otherFeedbackText", label: "Other Feedback" },
    { key: "followupDate", label: "Follow-Up Date" },
    { key: "callType", label: "Call Type" },
    { key: "parking", label: "Parking" },
    { key: "clientFeedback", label: "Client Feedback" },
    { key: "addedBy", label: "Added By" },
    { key: "status", label: "Status" },
  ];

  return (
    <>
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-4">
        <input
          type="text"
          placeholder="Customer Name"
          value={filters.customerName}
          onChange={(e) =>
            setFilters({ ...filters, customerName: e.target.value })
          }
          className="rounded border p-2"
        />
        <input
          type="text"
          placeholder="Customer Number"
          value={filters.customerNumber}
          onChange={(e) =>
            setFilters({ ...filters, customerNumber: e.target.value })
          }
          className="rounded border p-2"
        />
        <input
          type="month"
          value={filters.followupDate}
          onChange={(e) =>
            setFilters({ ...filters, followupDate: e.target.value })
          }
          className="w-40 rounded border p-2"
        />
        <select
          value={filters.callType}
          onChange={(e) => setFilters({ ...filters, callType: e.target.value })}
          className="rounded border p-2"
        >
          <option value="">Call Type?</option>
          <option value="Fresh Call">Fresh Call</option>
          <option value="Re-call">Re-call</option>
        </select>

        <select
          value={filters.customerFeedbackCategory}
          onChange={(e) => setFilters({ ...filters, customerFeedbackCategory: e.target.value })}
          className="rounded border p-2"
        >
          <option value="">Feedback Category?</option>
          <option value="Ringing/Busy">Ringing/Busy</option>
          <option value="Call back">Call back</option>
          <option value="Need With Cost Sheet Share">Need With Cost Sheet Share</option>
          <option value="Cost Sheet Shared">Cost Sheet Shared</option>
          <option value="Visit schedule">Visit schedule</option>
          <option value="Not Interested">Not Interested</option>
          <option value="Booking Done">Booking Done</option>
          <option value="Visit Done">Visit Done</option>
          <option value="Other">Other</option>
        </select>

        <button
          onClick={applyFilters}
          className="rounded bg-red-500 px-4 py-2 text-white"
        >
          Apply Filters
        </button>
        <button
          onClick={resetFilters}
          className="rounded bg-gray-400 px-4 py-2 text-white"
        >
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="relative rounded-lg bg-white p-4 shadow dark:bg-navy-700">
        <div className="overflow-x-auto">
          <div className="max-h-[500px] overflow-y-auto">
            <table className="min-w-full text-left text-sm text-gray-700 dark:text-gray-100">
              <thead className="text-black sticky top-0 z-20 border-b border-gray-200 bg-gray-100 text-xs font-bold dark:bg-gray-700 dark:text-white">
                <tr>
                  <th className="sticky left-0 top-0 z-30 whitespace-nowrap border bg-gray-100 px-3 py-2 dark:bg-gray-700">
                    SL No
                  </th>
                  {tableHeaders.map(({ label }, index) => (
                    <th
                      key={index}
                      className="whitespace-nowrap border px-3 py-2"
                    >
                      {label}
                    </th>
                  ))}
                  <th className="whitespace-nowrap border px-3 py-2">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {(() => {
                  const isLocalMode = isFiltered || !!search;
                  const displayedCustomers = isLocalMode
                    ? filteredCustomers.slice((localPage - 1) * 50, localPage * 50)
                    : filteredCustomers;
                  return displayedCustomers.length > 0 ? (
                  displayedCustomers.map((cust, index) => (
                    <tr
                      key={cust._id}
                      className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                    >
                      <td className="sticky left-0 z-10 whitespace-nowrap border bg-white px-3 py-2 dark:bg-navy-700">
                        {isLocalMode ? (localPage - 1) * 50 + index + 1 : (page - 1) * 50 + index + 1}
                      </td>

                      {tableHeaders.map(({ key }, idx) => (
                        <td
                          key={idx}
                          className="whitespace-nowrap border px-3 py-2"
                        >
                          {key === "status" ? (
                            <span
                              className={`rounded px-2 py-1 text-xs font-semibold ${cust[key]?.toLowerCase() === "active"
                                ? "text-green-700"
                                : "text-gray-600"
                                }`}
                            >
                              {cust[key] || "-"}
                            </span>
                          ) : key === "callType" ? (
                            cust[key] || "-"
                          ) : key === "addedBy" ? (
                            adminMap[cust?.addedBy] || cust?.addedBy || "-"
                          ) : key.includes("Date") ? (
                            formatDateTime(cust[key])
                          ) : (
                            cust[key] || "-"
                          )}
                        </td>
                      ))}

                      {/* Actions */}
                      <td className="flex flex-wrap gap-2 whitespace-nowrap border px-3 py-2">
                        <button
                          onClick={() => onViewVisits(cust)}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          View Visits
                        </button>

                        {/* Edit Button */}
                        <button
                          className={`${hasPermission("edit")
                            ? "text-blue-600 hover:text-blue-800"
                            : "text-gray-400 cursor-not-allowed opacity-60"
                            }`}
                          onClick={() => hasPermission("edit") && handleEditClick(cust)}
                          disabled={!hasPermission("edit")}
                          title={!hasPermission("edit") ? "No permission to edit" : "Edit customer"}
                        >
                          <FaEdit />
                        </button>

                        {/* Delete Button */}
                        <button
                          className={`${hasPermission("delete")
                            ? "text-red-600 hover:text-red-800"
                            : "text-gray-400 cursor-not-allowed opacity-60"
                            }`}
                          onClick={() => hasPermission("delete") && confirmDelete(cust._id)}
                          disabled={!hasPermission("delete")}
                          title={!hasPermission("delete") ? "No permission to delete" : "Delete customer"}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  )) ) : (
                  <tr>
                    <td
                      colSpan="100%"
                      className="py-4 text-center text-gray-500"
                    >
                      No matching customers found.
                    </td>
                  </tr>
                );})()}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {/* Pagination - unified for backend pages and local filter/search pages */}
      {(() => {
        const isLocalMode = isFiltered || !!search;
        const activePage = isLocalMode ? localPage : page;
        const activeTotal = isLocalMode ? Math.max(1, Math.ceil(filteredCustomers.length / 50)) : totalPages;
        const handleChange = isLocalMode ? setLocalPage : onPageChange;
        if (activeTotal < 1) return null;
        return (
          <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
            <button onClick={() => handleChange(1)} disabled={activePage === 1}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">«</button>
            <button onClick={() => handleChange(activePage - 1)} disabled={activePage === 1}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">Previous</button>

            {Array.from({ length: activeTotal }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === activeTotal || Math.abs(p - activePage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span key={`dots-${idx}`} className="px-2 text-gray-500 dark:text-gray-400">...</span>
                ) : (
                  <button key={item} onClick={() => handleChange(item)}
                    className={`rounded border px-3 py-1 text-sm ${activePage === item ? "bg-indigo-600 text-white border-indigo-600" : "text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700"}`}
                  >{item}</button>
                )
              )}

            <button onClick={() => handleChange(activePage + 1)} disabled={activePage === activeTotal}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">Next</button>
            <button onClick={() => handleChange(activeTotal)} disabled={activePage === activeTotal}
              className="rounded border px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700">»</button>
            <span className="text-sm text-gray-600 dark:text-white ml-2">
              Page {activePage} of {activeTotal}
              {isLocalMode && ` (${filteredCustomers.length} results)`}
            </span>
          </div>
        );
      })()}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="w-[300px] rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">
              Are you sure you want to Delete?
            </h2>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-white dark:hover:bg-white/20"
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {showEditForm && selectedCustomer && (
        <EditCustomerForm
          initialData={selectedCustomer}
          onClose={() => {
            setShowEditForm(false);
            setSelectedCustomer(null);
          }}
          onSuccess={() => {
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default CustomerTable;

